import { chord, noteHz, renderInStyle, tone } from "@client/utils/synth";
import { SYNTH_GLASS, SYNTH_WOOD } from "@client/utils/synth/constants";
import type { SynthSound } from "@client/utils/synth/types";

const BELL_PARTIALS = [
	{ ratio: 2.756, gain: 0.22, duration: 0.5 },
	{ ratio: 5.404, gain: 0.06, duration: 0.2 },
];

const GAME_START: SynthSound = {
	gain: 1,
	lowpass: 4_000,
	notes: [
		...[65, 72, 69, 76, 72, 79, 76, 83, 79, 84, 83, 88].map((midi, index) =>
			tone(noteHz(midi), index * 0.085, 0.25, {
				wave: "triangle",
				attack: 0.002,
				gain: 0.45,
			}),
		),
		...chord([41, 53, 64, 71, 76], 1, 2, {
			wave: "triangle",
			attack: 0.25,
			gain: 0.3,
		}),
		tone(noteHz(88), 1.02, 1.9, {
			wave: "sine",
			attack: 0.003,
			partials: BELL_PARTIALS,
			gain: 0.35,
		}),
	],
};

export const PLAYABLE_SOUND_SYNTHS = {
	join_room: renderInStyle(
		[
			tone(noteHz(72), 0, 0.15),
			tone(noteHz(76), 0.15, 0.15),
			tone(noteHz(79), 0.3, 0.15),
			tone(noteHz(84), 0.45, 0.6),
			tone(noteHz(60), 0, 0.9, { gain: 0.4 }),
		],
		SYNTH_WOOD,
	),
	leave_room: renderInStyle(
		[
			tone(noteHz(84), 0, 0.15),
			tone(noteHz(79), 0.15, 0.15),
			tone(noteHz(76), 0.3, 0.15),
			tone(noteHz(72), 0.45, 0.6),
			tone(noteHz(67), 0, 0.9, { gain: 0.35 }),
		],
		SYNTH_WOOD,
	),
	notification: renderInStyle(
		[
			tone(noteHz(86), 0, 0.15, { gain: 0.8 }),
			tone(noteHz(81), 0.15, 0.3, { gain: 0.8 }),
		],
		SYNTH_WOOD,
	),
	game_start: GAME_START,
	turn_start: renderInStyle(
		[
			tone(noteHz(79), 0, 0.15),
			tone(noteHz(84), 0.18, 0.15),
			tone(noteHz(88), 0.36, 0.4, { gain: 0.8 }),
		],
		SYNTH_GLASS,
	),
	timer: renderInStyle(
		[tone(noteHz(81), 0, 0.035, { gain: 0.7 })],
		SYNTH_GLASS,
	),
	auction_start: renderInStyle(
		[
			tone(noteHz(81), 0, 0.08),
			tone(noteHz(81), 0.2, 0.08),
			tone(noteHz(74), 0.5, 0.15),
			tone(noteHz(76), 0.65, 0.15),
			tone(noteHz(79), 0.8, 0.15),
			tone(noteHz(81), 0.95, 0.2),
			tone(noteHz(86), 1.15, 0.7),
			tone(noteHz(62), 0.5, 1.2, { gain: 0.35 }),
			tone(noteHz(69), 1.15, 0.7, { gain: 0.3 }),
		],
		SYNTH_GLASS,
	),
	deal_start: renderInStyle(
		[
			tone(noteHz(67), 0, 0.15),
			tone(noteHz(74), 0.15, 0.15),
			tone(noteHz(79), 0.3, 0.2),
			tone(noteHz(81), 0.5, 0.6),
			tone(noteHz(62), 0.3, 0.8, { gain: 0.35 }),
		],
		SYNTH_GLASS,
	),
	deal_response: renderInStyle(
		[
			tone(noteHz(88), 0, 0.15, { gain: 0.8 }),
			tone(noteHz(86), 0.15, 0.15, { gain: 0.8 }),
			tone(noteHz(84), 0.3, 0.2),
			tone(noteHz(79), 0.5, 0.5),
			tone(noteHz(60), 0.3, 0.7, { gain: 0.35 }),
		],
		SYNTH_GLASS,
	),
} satisfies Record<string, SynthSound>;

export type PlayableSound = keyof typeof PLAYABLE_SOUND_SYNTHS;

export const PLAYABLE_SOUNDS = Object.keys(
	PLAYABLE_SOUND_SYNTHS,
) as PlayableSound[];
